import React, { useState, useEffect } from 'react';
import { updateTask, deleteTask, moveTask, getUserNames } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTimer } from '../lib/TimerContext';
import { formatTime } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Clock,
  Calendar,
  User,
  Trash2,
  Save,
  X,
  Play,
  ChevronRight,
  FolderKanban,
  Building2,
  Globe,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_OPTIONS = [
  { value: 'internal', label: 'Interní', icon: Building2, color: 'text-gray-700' },
  { value: 'external', label: 'Externí', icon: Globe, color: 'text-blue-600' },
];

const STATUS_FLOW = {
  todo: { next: 'in_progress', label: 'Rozpracovat' },
  in_progress: { next: 'done', label: 'Dokončit' },
  done: { prev: 'in_progress', label: 'Vrátit' },
};

const STATUS_LABELS = {
  todo: 'K řešení',
  in_progress: 'Rozpracováno',
  done: 'Hotovo',
};


const TaskDetailDialog = ({ 
  task, 
  open, 
  onOpenChange, 
  onTaskUpdated, 
  projects = [],
  readOnly = false 
}) => {
  const { user } = useAuth();
  const { isRunning, startTimer, timerData } = useTimer();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'todo',
    task_type: 'internal',
    deadline: '',
    assigned_to: [],
    estimated_minutes: '',
    checklist: [],
  });
  const [newCheckItem, setNewCheckItem] = useState('');

  // Load users for assignment
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getUserNames();
        setUsers(res.data || []);
      } catch (error) {
        // Non-admin may not have access
        setUsers([]);
      }
    };
    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Reset form when task changes
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'todo',
        task_type: task.task_type || 'internal',
        deadline: task.deadline || '',
        assigned_to: task.assigned_to || [],
        estimated_minutes: task.estimated_minutes || '',
        checklist: task.checklist || [],
      });
      setNewCheckItem('');
      setIsEditing(false);
    }
  }, [task]);

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Název úkolu je povinný');
      return;
    }
    
    setLoading(true);
    try {
      const updateData = {
        title: formData.title,
        description: formData.description || null,
        status: formData.status,
        task_type: formData.task_type,
        deadline: formData.deadline || null,
        assigned_to: formData.assigned_to.length > 0 ? formData.assigned_to : null,
        estimated_minutes: formData.estimated_minutes ? parseInt(formData.estimated_minutes) : null,
        checklist: formData.checklist,
      };
      
      await updateTask(task.id, updateData);
      toast.success('Úkol uložen');
      setIsEditing(false);
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteTask(task.id);
      toast.success('Úkol smazán');
      setDeleteDialogOpen(false);
      onOpenChange(false);
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při mazání');
    } finally {
      setLoading(false);
    }
  };

  const handleNextStage = async () => {
    const flow = STATUS_FLOW[task.status];
    if (!flow) return;
    
    const newStatus = flow.next || flow.prev;
    if (!newStatus) return;
    
    setLoading(true);
    try {
      await moveTask(task.id, { new_status: newStatus, new_order: 0 });
      toast.success(`Úkol přesunut do "${STATUS_LABELS[newStatus]}"`);
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      toast.error('Chyba při přesunu úkolu');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTimer = async () => {
    try {
      await startTimer({
        project_id: task.project_id,
        project_name: task.project_name,
        task_id: task.id,
        task_name: task.title,
        description: '',
      });
    } catch (error) {
      // Error handled in TimerContext
    }
  };

  const toggleUserAssignment = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(userId)
        ? prev.assigned_to.filter(id => id !== userId)
        : [...prev.assigned_to, userId]
    }));
  };

  const addCheckItem = () => {
    const text = newCheckItem.trim();
    if (!text) return;
    setFormData(prev => ({
      ...prev,
      checklist: [...prev.checklist, { text, done: false }],
    }));
    setNewCheckItem('');
  };

  const removeCheckItem = (idx) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== idx),
    }));
  };

  const toggleCheckItem = (idx) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.map((item, i) =>
        i === idx ? { ...item, done: !item.done } : item
      ),
    }));
  };

  const toggleCheckItemImmediate = async (idx) => {
    const previous = task.checklist || [];
    const updated = previous.map((item, i) =>
      i === idx ? { ...item, done: !item.done } : item
    );
    // Optimistic update via onTaskUpdated is not possible without local task state,
    // so we just save and refresh; on error we show the toast (list stays as-is from server)
    try {
      await updateTask(task.id, { checklist: updated });
      if (onTaskUpdated) onTaskUpdated();
    } catch {
      toast.error('Chyba při ukládání');
      if (onTaskUpdated) onTaskUpdated(); // re-fetch to restore correct server state
    }
  };

  const isTimerRunningOnThis = isRunning && timerData.task_id === task?.id;
  const canEdit = !readOnly && user && (user.is_admin || task?.created_by === user.id);
  const canDelete = canEdit;
  const statusFlow = task ? STATUS_FLOW[task.status] : null;

  if (!task) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-oswald text-xl uppercase tracking-tight flex items-center justify-between">
              <span>{isEditing ? 'Upravit úkol' : 'Detail úkolu'}</span>
              {!isEditing && canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="font-oswald uppercase text-xs"
                >
                  Upravit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Title */}
            {isEditing ? (
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Název *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="task-detail-title"
                />
              </div>
            ) : (
              <div>
                <h2 className="font-oswald text-2xl font-bold">{task.title}</h2>
                <div className="flex items-center gap-2 text-sm text-gray-500 font-serif mt-1">
                  <FolderKanban size={14} />
                  <span>{task.project_name}</span>
                  {task.client_name && (
                    <>
                      <span>•</span>
                      <span>{task.client_name}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Status & Type Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Status</Label>
                {isEditing ? (
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger className="border-2 border-gray-200 rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">K řešení</SelectItem>
                      <SelectItem value="in_progress">Rozpracováno</SelectItem>
                      <SelectItem value="done">Hotovo</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className={`inline-block px-3 py-1 text-sm font-oswald uppercase border-2 ${
                    task.status === 'done' ? 'bg-green-100 border-green-300' :
                    task.status === 'in_progress' ? 'bg-blue-100 border-blue-300' :
                    'bg-gray-100 border-gray-300'
                  }`}>
                    {STATUS_LABELS[task.status]}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Typ</Label>
                {isEditing ? (
                  <div className="flex gap-2">
                    {TYPE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, task_type: opt.value })}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-oswald uppercase border-2 transition-colors ${
                          formData.task_type === opt.value
                            ? opt.value === 'internal'
                              ? 'bg-black text-white border-black'
                              : 'bg-[#006CB6] text-white border-[#006CB6]'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <opt.icon size={12} />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    {(task.task_type === 'external') ? (
                      <><Globe size={14} className="text-blue-600" /><span className="font-serif text-blue-600">Externí</span></>
                    ) : (
                      <><Building2 size={14} className="text-gray-700" /><span className="font-serif text-gray-700">Interní</span></>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="font-oswald uppercase text-xs tracking-wide">Popis</Label>
              {isEditing ? (
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="border-2 border-gray-200 focus:border-black rounded-none min-h-[150px]"
                  placeholder="Popis úkolu..."
                  data-testid="task-detail-description"
                />
              ) : (
                <div className="font-serif text-gray-700 bg-gray-50 p-3 min-h-[60px] whitespace-pre-wrap">
                  {task.description || <span className="text-gray-400 italic">Bez popisu</span>}
                </div>
              )}
            </div>

            {/* Checklist */}
            {(isEditing || (task.checklist && task.checklist.length > 0)) && (
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide flex items-center justify-between">
                  <span>To-do</span>
                  {!isEditing && task.checklist && task.checklist.length > 0 && (
                    <span className="text-gray-400 font-serif normal-case text-xs">
                      {task.checklist.filter(i => i.done).length}/{task.checklist.length}
                    </span>
                  )}
                </Label>

                {/* Progress bar (view mode) */}
                {!isEditing && task.checklist && task.checklist.length > 0 && (
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-black transition-all"
                      style={{ width: `${Math.round((task.checklist.filter(i => i.done).length / task.checklist.length) * 100)}%` }}
                    />
                  </div>
                )}

                <div className="space-y-1">
                  {(isEditing ? formData.checklist : task.checklist || []).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 group">
                      <Checkbox
                        checked={item.done}
                        onCheckedChange={() => isEditing ? toggleCheckItem(idx) : toggleCheckItemImmediate(idx)}
                        className="border-2 border-gray-300 rounded-none"
                      />
                      <span className={`font-serif text-sm flex-1 ${item.done ? 'line-through text-gray-400' : ''}`}>
                        {item.text}
                      </span>
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => removeCheckItem(idx)}
                          className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {isEditing && (
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newCheckItem}
                      onChange={(e) => setNewCheckItem(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCheckItem(); } }}
                      placeholder="Přidat položku..."
                      className="border-2 border-gray-200 focus:border-black rounded-none text-sm h-8"
                    />
                    <button
                      type="button"
                      onClick={addCheckItem}
                      className="border-2 border-gray-200 hover:border-black px-2 transition-colors"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Deadline & Estimated Time Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                  <Calendar size={12} /> Deadline
                </Label>
                {isEditing ? (
                  <Input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                  />
                ) : (
                  <div className={`font-serif ${task.deadline && new Date(task.deadline) < new Date() && task.status !== 'done' ? 'text-red-600 font-bold' : ''}`}>
                    {task.deadline || <span className="text-gray-400">Není nastaven</span>}
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                  <Clock size={12} /> Odhadovaný čas (min)
                </Label>
                {isEditing ? (
                  <Input
                    type="number"
                    min="0"
                    value={formData.estimated_minutes}
                    onChange={(e) => setFormData({ ...formData, estimated_minutes: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    placeholder="např. 120"
                  />
                ) : (
                  <div className="font-serif">
                    {task.estimated_minutes ? formatTime(task.estimated_minutes) : <span className="text-gray-400">Není nastaven</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Time Stats (read-only) */}
            <div className="bg-gray-50 border-2 border-gray-200 p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-oswald text-xs uppercase text-gray-500">Odpracováno</div>
                  <div className="font-oswald text-xl font-bold">{formatTime(task.total_time_minutes || 0)}</div>
                </div>
                {task.estimated_minutes > 0 && (
                  <div>
                    <div className="font-oswald text-xs uppercase text-gray-500">Využití odhadu</div>
                    <div className={`font-oswald text-xl font-bold ${
                      task.total_time_minutes > task.estimated_minutes ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {Math.round((task.total_time_minutes / task.estimated_minutes) * 100)}%
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Assigned Users */}
            {(isEditing || (task.assigned_names && task.assigned_names.length > 0)) && (
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                  <User size={12} /> Přiřazení uživatelé
                </Label>
                {isEditing && users.length > 0 ? (
                  <div className="border-2 border-gray-200 p-3 max-h-32 overflow-y-auto space-y-2">
                    {users.map((u) => (
                      <div key={u.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`detail-user-${u.id}`}
                          checked={formData.assigned_to.includes(u.id)}
                          onCheckedChange={() => toggleUserAssignment(u.id)}
                        />
                        <label htmlFor={`detail-user-${u.id}`} className="font-serif text-sm cursor-pointer">
                          {u.name}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="font-serif">
                    {task.assigned_names?.join(', ') || <span className="text-gray-400">Nikdo není přiřazen</span>}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleSave}
                    disabled={loading}
                    className="bg-black text-white font-oswald uppercase tracking-wide rounded-none hover:bg-gray-800"
                    data-testid="task-detail-save"
                  >
                    <Save size={16} className="mr-2" />
                    Uložit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                    className="font-oswald uppercase rounded-none border-2 border-gray-200"
                  >
                    Zrušit
                  </Button>
                </>
              ) : (
                <>
                  {/* Start Timer */}
                  <Button
                    onClick={handleStartTimer}
                    disabled={isRunning}
                    className={`font-oswald uppercase tracking-wide rounded-none border-2 ${
                      isTimerRunningOnThis 
                        ? 'bg-green-100 border-green-500 text-green-700'
                        : 'bg-[#F4E600] border-black text-black hover:bg-yellow-400'
                    }`}
                    data-testid="task-detail-start-timer"
                  >
                    <Play size={16} className="mr-2" />
                    {isTimerRunningOnThis ? 'Timer běží' : isRunning ? 'Jiný timer běží' : 'Spustit timer'}
                  </Button>
                  
                  {/* Next Stage */}
                  {statusFlow && (
                    <Button
                      onClick={handleNextStage}
                      disabled={loading}
                      className="bg-[#006CB6] text-white font-oswald uppercase tracking-wide rounded-none hover:bg-blue-700"
                      data-testid="task-detail-next-stage"
                    >
                      <ChevronRight size={16} className="mr-1" />
                      {statusFlow.label}
                    </Button>
                  )}
                  
                  {/* Delete */}
                  {canDelete && (
                    <Button
                      variant="outline"
                      onClick={() => setDeleteDialogOpen(true)}
                      className="text-red-500 border-red-200 hover:bg-red-50 font-oswald uppercase rounded-none ml-auto"
                      data-testid="task-detail-delete"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Smazat
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-oswald uppercase">Smazat úkol?</AlertDialogTitle>
            <AlertDialogDescription className="font-serif">
              Opravdu chcete smazat úkol &ldquo;{task?.title}&rdquo;? Tato akce je nevratná a smaže i všechny související časové záznamy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-oswald uppercase rounded-none border-2 border-gray-200">
              Zrušit
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={loading}
              className="bg-red-500 font-oswald uppercase rounded-none hover:bg-red-600"
            >
              Smazat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TaskDetailDialog;
